import React, { useEffect, useState, useRef } from 'react';
import { TruckIcon, BoxIcon, BarChart3Icon, ShieldIcon, ZapIcon, UsersIcon, GlobeIcon, MenuIcon, XIcon, SparkleIcon, ArrowRightIcon, ArrowLeftIcon, ChevronDownIcon, LayersIcon, LightbulbIcon, PresentationIcon, GemIcon, StarIcon, MailIcon, RocketIcon, ExternalLinkIcon, FacebookIcon, TwitterIcon, InstagramIcon, LinkedinIcon, YoutubeIcon, CheckCircleIcon, SmartphoneIcon, TabletIcon, LaptopIcon, ServerIcon, SettingsIcon, LayoutGridIcon } from 'lucide-react';
import { translations, faqTranslations, testimonialTranslations } from './translations';
import WaitlistForm from './components/WaitlistForm';
import { Link } from 'react-router-dom';
// Update CreativeCard component to use solid colors instead of gradients
const CreativeCard = ({
  children,
  className = ''
}) => {
  return <div className={`relative group overflow-hidden ${className}`} data-id="element-0">
      <div className="absolute inset-0 bg-gray-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl" data-id="element-1"></div>
      <div className="relative bg-white dark:bg-gray-900 rounded-xl p-6 h-full border border-gray-200 shadow-lg z-10" data-id="element-2">
        {children}
      </div>
    </div>;
};
// Update Pill component to use solid colors instead of gradients
const Pill = ({
  children,
  className = ''
}) => {
  return <div className={`inline-flex items-center rounded-full px-4 py-1.5 bg-white shadow-md relative overflow-hidden border border-gray-100 ${className}`} data-id="element-3">
      <span className="relative z-10" data-id="element-4">{children}</span>
    </div>;
};
// Update GlowingButton component to use solid colors instead of gradients
const GlowingButton = ({
  children,
  onClick,
  className = ''
}) => {
  return <button onClick={onClick} className={`relative px-6 py-3 overflow-hidden group rounded-xl ${className}`} data-id="element-5">
      <span className="absolute inset-0 bg-[#D93B28] rounded-xl z-0" data-id="element-6"></span>
      <span className="relative flex items-center justify-center text-white font-medium z-10" data-id="element-7">
        {children}
      </span>
    </button>;
};
// Update NeumorphicInput component to use solid colors instead of gradients
const NeumorphicInput = ({
  type,
  placeholder,
  value,
  onChange,
  required,
  className = ''
}) => {
  return <div className="relative" data-id="element-8">
      <input type={type} placeholder={placeholder} value={value} onChange={onChange} required={required} className={`w-full px-6 py-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#D93B28]/50 shadow-sm bg-white text-gray-800 transition-all ${className}`} data-id="element-9" />
    </div>;
};
// Update CreativeDivider component to use solid colors instead of gradients
const CreativeDivider = () => {
  return <div className="relative flex items-center justify-center py-8" data-id="element-10">
      <div className="absolute w-full h-px bg-gray-300" data-id="element-11"></div>
      <div className="absolute flex space-x-4" data-id="element-12">
        <div className="w-2 h-2 rounded-full bg-[#D93B28] animate-pulse" style={{
        animationDelay: '0s'
      }} data-id="element-13"></div>
        <div className="w-2 h-2 rounded-full bg-[#D93B28] animate-pulse" style={{
        animationDelay: '0.3s'
      }} data-id="element-14"></div>
        <div className="w-2 h-2 rounded-full bg-[#D93B28] animate-pulse" style={{
        animationDelay: '0.6s'
      }} data-id="element-15"></div>
      </div>
    </div>;
};
// Add FloatingIcons component definition
const FloatingIcons = ({
  icons
}) => {
  return <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-16">
      {icons.map((icon, index) => <div key={index} className="absolute animate-float" style={{
      top: `${icon.top}%`,
      left: `${icon.left}%`,
      animationDelay: `${icon.delay}s`,
      animationDuration: `${icon.duration}s`,
      opacity: icon.opacity
    }} data-id="element-17">
          {icon.element}
        </div>)}
    </div>;
};
export function App() {
  const [email, setEmail] = useState('');
  const [faqOpen, setFaqOpen] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  const [cursorPosition, setCursorPosition] = useState({
    x: 0,
    y: 0
  });
  const [scrollY, setScrollY] = useState(0);
  const [language, setLanguage] = useState('en');
  const heroRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const benefitsRef = useRef<HTMLDivElement>(null);
  const testimonialsRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);
  const t = translations[language as keyof typeof translations];
  const faqs = faqTranslations[language as keyof typeof faqTranslations];
  const testimonials = testimonialTranslations[language as keyof typeof testimonialTranslations];
  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ar' : 'en');
  };
  const toggleFaq = (index: number) => {
    setFaqOpen(faqOpen === index ? null : index);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Open Google Forms link in new tab
    window.open('https://forms.gle/CGf7e8wf6kjPnQyH8', '_blank');
  };
  const openWaitlistForm = () => {
    // Open Google Forms link in new tab
    window.open('https://forms.gle/CGf7e8wf6kjPnQyH8', '_blank');
  };
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
      // Update active section based on scroll position
      const sections = ['preview', 'features', 'benefits', 'testimonials', 'faq'];
      let current = '';
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            current = section;
            break;
          }
        }
      }
      setActiveSection(current);
    };
    const handleMouseMove = (e: MouseEvent) => {
      if (heroRef.current) {
        const {
          left,
          top,
          width,
          height
        } = heroRef.current.getBoundingClientRect();
        const x = (e.clientX - left) / width - 0.5;
        const y = (e.clientY - top) / height - 0.5;
        setMousePosition({
          x,
          y
        });
      }
      // Global cursor position for other effects
      setCursorPosition({
        x: e.clientX,
        y: e.clientY
      });
    };
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  const calculateParallax = (ref: React.RefObject<HTMLDivElement>, speed: number = 0.1) => {
    if (!ref.current) return 0;
    const rect = ref.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const centerPosition = rect.top + rect.height / 2;
    const fromCenter = centerPosition - windowHeight / 2;
    return -fromCenter * speed;
  };
  const parallaxHero = scrollY * 0.3;
  const parallaxFeatures = calculateParallax(featuresRef, 0.08);
  const parallaxBenefits = calculateParallax(benefitsRef, 0.06);
  const parallaxTestimonials = calculateParallax(testimonialsRef, 0.05);
  const parallaxCta = calculateParallax(ctaRef, 0.1);
  const isRTL = language === 'ar';
  const dir = isRTL ? 'rtl' : 'ltr';
  const fontClass = isRTL ? 'font-arabic' : 'font-sans';
  // Utility function to flip margins/paddings for RTL
  const rtlFlip = (className: string) => {
    if (!isRTL) return className;
    return className.replace(/mr-(\d+|auto)/g, 'ml-temp').replace(/ml-(\d+|auto)/g, 'mr-$1').replace(/ml-temp/g, 'ml-$1').replace(/pr-(\d+|auto)/g, 'pl-temp').replace(/pl-(\d+|auto)/g, 'pr-$1').replace(/pl-temp/g, 'pl-$1').replace(/right-/g, 'left-temp').replace(/left-/g, 'right-').replace(/left-temp/g, 'left-');
  };
  // Floating icons for hero section
  const heroIcons = [{
    top: 15,
    left: 10,
    delay: 0,
    duration: 6,
    opacity: 0.6,
    element: <TruckIcon size={24} className="text-[#D93B28]/40" data-id="element-18" />
  }, {
    top: 70,
    left: 15,
    delay: 1,
    duration: 8,
    opacity: 0.5,
    element: <BoxIcon size={20} className="text-[#D93B28]/40" data-id="element-19" />
  }, {
    top: 25,
    left: 85,
    delay: 2,
    duration: 7,
    opacity: 0.6,
    element: <BarChart3Icon size={22} className="text-[#D93B28]/40" data-id="element-20" />
  }, {
    top: 65,
    left: 80,
    delay: 1.5,
    duration: 9,
    opacity: 0.5,
    element: <ShieldIcon size={18} className="text-[#D93B28]/40" data-id="element-21" />
  }, {
    top: 40,
    left: 25,
    delay: 0.5,
    duration: 10,
    opacity: 0.4,
    element: <ZapIcon size={16} className="text-[#D93B28]/40" data-id="element-22" />
  }, {
    top: 50,
    left: 90,
    delay: 2.5,
    duration: 8,
    opacity: 0.5,
    element: <UsersIcon size={18} className="text-[#D93B28]/40" data-id="element-23" />
  }];
  return <div className={`flex flex-col min-h-screen w-full bg-gray-50 overflow-hidden ${fontClass}`} dir={dir} data-id="element-24">
      {/* Simplified cursor follower effect with solid colors */}
      <div className="fixed w-12 h-12 rounded-full bg-[#D93B28]/20 pointer-events-none z-50 blur-md hidden md:block" style={{
      transform: `translate(${cursorPosition.x - 24}px, ${cursorPosition.y - 24}px)`,
      transition: 'transform 0.15s ease-out'
    }} data-id="element-25"></div>
      <div className="fixed w-4 h-4 rounded-full bg-[#D93B28] pointer-events-none z-50 hidden md:block" style={{
      transform: `translate(${cursorPosition.x - 2}px, ${cursorPosition.y - 2}px)`,
      transition: 'transform 0.05s linear',
      opacity: 0.7
    }} data-id="element-26"></div>
      <div className="fixed w-2 h-2 rounded-full bg-white pointer-events-none z-50 hidden md:block" style={{
      transform: `translate(${cursorPosition.x - 1}px, ${cursorPosition.y - 1}px)`,
      transition: 'transform 0.02s linear',
      opacity: 0.9
    }} data-id="element-27"></div>
      {/* Simplified decorative background elements with solid colors */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden" data-id="element-28">
        <div className="absolute top-0 left-0 w-[800px] h-[800px] bg-[#D93B28]/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 animate-pulse-slow" style={{
        animationDuration: '15s'
      }} data-id="element-29"></div>
        <div className="absolute top-[30%] right-0 w-[600px] h-[600px] bg-[#D34A44]/10 rounded-full blur-3xl translate-x-1/2 animate-pulse-slow" style={{
        animationDuration: '20s',
        animationDelay: '2s'
      }} data-id="element-30"></div>
        <div className="absolute bottom-0 left-[20%] w-[500px] h-[500px] bg-[#D93B28]/10 rounded-full blur-3xl animate-pulse-slow" style={{
        animationDuration: '25s',
        animationDelay: '1s'
      }} data-id="element-31"></div>
        {/* Simplified geometric shapes with solid colors */}
        <div className="absolute top-[15%] right-[10%] w-32 h-32 border border-[#D93B28]/20 rounded-full opacity-50 animate-spin-slow" data-id="element-32"></div>
        <div className="absolute top-[20%] right-[12%] w-16 h-16 border border-[#D34A44]/30 rounded-full opacity-50 animate-spin-slow" style={{
        animationDirection: 'reverse',
        animationDuration: '30s'
      }} data-id="element-33"></div>
        <div className="absolute top-[60%] left-[5%] w-24 h-24 border border-[#D93B28]/20 rounded-full opacity-50 animate-spin-slow" style={{
        animationDuration: '25s'
      }} data-id="element-34"></div>
        {/* Simplified dots pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(#D93B28_1px,transparent_1px)] opacity-[0.03] [background-size:20px_20px]" data-id="element-35"></div>
      </div>
      {/* Enhanced Header/Navigation */}
      <header className={`sticky top-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white/90 backdrop-blur-lg shadow-lg py-3' : 'bg-transparent py-5'}`} data-id="element-36">
        <div className="container mx-auto px-4 sm:px-6 flex items-center justify-between relative" data-id="element-37">
          <div className="flex items-center" data-id="element-38">
            <div className="relative group" data-id="element-39">
              <div className="absolute inset-0 bg-[#D93B28]/20 rounded-lg blur-md group-hover:blur-xl transition-all duration-300" data-id="element-40"></div>
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2FH5qpVtZh9D5YZVGYt4z6/1762799569364-removebg-preview.png" alt="Logix Logo" className={`h-12 w-auto relative z-10 transition-all duration-300 ${rtlFlip('mr-0')}`} data-id="element-41" />
            </div>
          </div>
          {/* Language Switcher */}
          <div className="flex items-center z-[60] relative group" data-id="element-42">
            <button onClick={toggleLanguage} className="px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full text-sm font-medium text-gray-700 hover:bg-gray-100 border border-gray-200 shadow-sm flex items-center transition-all hover:shadow-md relative overflow-hidden" aria-label={language === 'en' ? 'Switch to Arabic' : 'Switch to English'} data-id="element-43">
              <div className="absolute inset-0 bg-[#D93B28]/5 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-44"></div>
              <GlobeIcon size={14} className={`${rtlFlip('mr-2')} flex-shrink-0 text-[#D93B28]`} data-id="element-45" />
              <span className="relative" data-id="element-46">{t.switchToArabic}</span>
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#D93B28] group-hover:w-full transition-all duration-300" data-id="element-47"></span>
            </button>
          </div>
          {/* Mobile Menu Button */}
          <button className="md:hidden z-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#D93B28]/50 rounded-md p-1" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} data-id="element-48">
            {mobileMenuOpen ? <XIcon size={24} data-id="element-49" /> : <MenuIcon size={24} data-id="element-50" />}
          </button>
          {/* Navigation */}
          <nav className={`
            ${mobileMenuOpen ? 'absolute top-full left-0 right-0 bg-white/95 backdrop-blur-lg shadow-lg p-8 flex flex-col space-y-6 md:relative md:bg-transparent md:shadow-none md:p-0 md:flex-row md:space-y-0 border-t border-gray-100 md:border-t-0 animate-fade-in' : 'hidden'} md:flex md:items-center md:space-x-10 ${isRTL ? 'md:space-x-reverse' : ''}`} data-id="element-51">
            {['features', 'benefits', 'preview', 'testimonials', 'faq'].map((section, index) => <a key={section} href={`#${section}`} className={`text-gray-700 hover:text-[#D93B28] transition-colors text-sm uppercase font-medium tracking-wide relative group overflow-hidden ${activeSection === section ? 'text-[#D93B28] font-semibold' : ''}`} onClick={() => setMobileMenuOpen(false)} style={{
            transitionDelay: `${index * 50}ms`
          }} data-id="element-52">
                  <span className="relative z-10 flex items-center" data-id="element-53">
                    <span className="capitalize" data-id="element-54">
                      {t[section as keyof typeof t]}
                    </span>
                    {activeSection === section && <SparkleIcon size={12} className={`${rtlFlip('ml-1')} text-[#D34A44]`} data-id="element-55" />}
                  </span>
                  <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-[#D93B28] transition-all duration-300 ${activeSection === section ? 'w-full' : 'group-hover:w-full'}`} data-id="element-56"></span>
                  <span className="absolute inset-0 bg-[#D93B28]/5 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" data-id="element-57"></span>
                </a>)}
          </nav>
          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-4" data-id="element-58">
            <button className="px-5 py-2.5 rounded-lg bg-white text-gray-700 font-medium hover:bg-gray-100 transition-all text-sm border border-gray-200 shadow-sm hover:shadow-md relative overflow-hidden group" data-id="element-59">
              <span className="relative z-10 flex items-center" data-id="element-60">
                {t.login}
                <SparkleIcon size={14} className="ml-1.5 opacity-0 group-hover:opacity-100 transition-opacity text-[#D34A44]" data-id="element-61" />
              </span>
              <span className="absolute inset-0 bg-[#D93B28]/5 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left" data-id="element-62"></span>
            </button>
            <GlowingButton onClick={openWaitlistForm} className="text-sm" data-id="element-63">
              <span className="flex items-center" data-id="element-64">
                {t.joinWaitlist}
                {isRTL ? <ArrowLeftIcon size={14} className="mr-1.5 transform group-hover:-translate-x-1 transition-transform" data-id="element-65" /> : <ArrowRightIcon size={14} className="ml-1.5 transform group-hover:translate-x-1 transition-transform" data-id="element-66" />}
              </span>
            </GlowingButton>
          </div>
        </div>
      </header>
      <main className="flex-grow relative z-10" data-id="element-67">
        {/* Hero Section */}
        <section ref={heroRef} className="py-20 md:py-32 px-4 sm:px-6 bg-white relative overflow-hidden" data-id="element-68">
          {/* Decorative elements with solid colors */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#D93B28]/15 rounded-full blur-2xl" data-id="element-69"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#D34A44]/15 rounded-full blur-2xl" data-id="element-70"></div>
          {/* Floating elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-71">
            <div className={`absolute top-[15%] ${isRTL ? 'right-[10%]' : 'left-[10%]'} animate-float`} style={{
            animationDelay: '0.5s',
            animationDuration: '7s'
          }} data-id="element-72">
              <div className="w-16 h-16 text-[#D34A44]/40" data-id="element-73">
                <TruckIcon size={32} data-id="element-74" />
              </div>
            </div>
            <div className={`absolute top-[60%] ${isRTL ? 'right-[5%]' : 'left-[5%]'} animate-float`} style={{
            animationDelay: '1.2s',
            animationDuration: '9s'
          }} data-id="element-75">
              <div className="w-24 h-24 border border-[#D93B28]/20 rounded-lg rotate-12 opacity-50" data-id="element-76"></div>
            </div>
            <div className={`absolute top-[25%] ${isRTL ? 'left-[10%]' : 'right-[10%]'} animate-float`} style={{
            animationDelay: '0.8s',
            animationDuration: '8s'
          }} data-id="element-77">
              <div className="w-10 h-10 text-[#D93B28]/40" data-id="element-78">
                <BarChart3Icon size={24} data-id="element-79" />
              </div>
            </div>
            <div className={`absolute bottom-[20%] ${isRTL ? 'left-[15%]' : 'right-[15%]'} animate-float`} style={{
            animationDelay: '1.5s',
            animationDuration: '10s'
          }} data-id="element-80">
              <div className="w-20 h-20 text-[#D34A44]/30" data-id="element-81">
                <SparkleIcon size={20} className={`absolute top-0 ${isRTL ? 'left-0' : 'right-0'}`} data-id="element-82" />
              </div>
            </div>
            <div className={`absolute top-[40%] ${isRTL ? 'left-[30%]' : 'right-[30%]'} animate-float`} style={{
            animationDelay: '2s',
            animationDuration: '6s'
          }} data-id="element-83">
              <div className="w-8 h-8 rounded-full bg-[#D34A44]/10" data-id="element-84"></div>
            </div>
          </div>
          <div className="container mx-auto max-w-6xl text-center relative" style={{
          transform: `translateY(${-parallaxHero * 0.5}px)`
        }} data-id="element-85">
            {/* Beta badge */}
            <div className="inline-flex items-center bg-white/70 backdrop-blur-md px-5 py-2.5 rounded-full mb-10 border border-gray-200/50 shadow-md relative overflow-hidden group" data-id="element-86">
              <span className="absolute inset-0 bg-[#D93B28]/5 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-87"></span>
              <span className={`relative z-10 text-xs font-semibold bg-[#D93B28] text-white rounded-full px-3 py-1 ${rtlFlip('mr-2')} shadow-sm`} data-id="element-88">
                {t.beta}
              </span>
              <span className="relative z-10 text-sm text-[#D93B28] font-medium" data-id="element-89">
                {t.comingSoon}
              </span>
              <ArrowRightIcon size={14} className="relative z-10 ml-2 text-[#D34A44]" data-id="element-90" />
            </div>
            {/* Hero title */}
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold text-gray-900 mb-6 leading-tight relative tracking-tight" data-id="element-91">
              <span className="inline-block" data-id="element-92">{t.heroTitle1} </span>
              <span className="inline-block text-[#D93B28]" data-id="element-93">
                {t.heroTitle2}
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-94">
              {t.heroSubtitle}
            </p>
            {/* Email form */}
            <form onSubmit={handleSubmit} className="flex flex-col md:flex-row items-center justify-center gap-4 max-w-lg mx-auto relative" data-id="element-95">
              <div className="relative w-full md:flex-1" data-id="element-96">
                <NeumorphicInput type="email" placeholder={t.emailPlaceholder} value={email} onChange={e => setEmail(e.target.value)} required={true} data-id="element-97" />
              </div>
              <GlowingButton onClick={() => {}} className="w-full md:w-auto" data-id="element-98">
                <span className="flex items-center justify-center font-semibold" data-id="element-99">
                  {t.getEarlyAccess}
                  <ArrowRightIcon size={16} className="ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-100" />
                </span>
              </GlowingButton>
            </form>
            {/* Benefits list */}
            <div className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-3" data-id="element-101">
              {[{
              icon: <CheckCircleIcon size={18} className="text-[#D93B28]" data-id="element-102" />,
              text: t.freeOnboarding
            }, {
              icon: <CheckCircleIcon size={18} className="text-[#D93B28]" data-id="element-103" />,
              text: t.noCreditCard
            }, {
              icon: <CheckCircleIcon size={18} className="text-[#D93B28]" data-id="element-104" />,
              text: t.cancelAnytime
            }].map((item, i) => <div key={i} className="flex items-center group" data-id="element-105">
                  <div className={`${rtlFlip('mr-2')} transition-transform group-hover:scale-110`} data-id="element-106">
                    {item.icon}
                  </div>
                  <span className="text-gray-600" data-id="element-107">{item.text}</span>
                </div>)}
            </div>
          </div>
        </section>
        {/* Creative divider */}
        <CreativeDivider data-id="element-108" />
        {/* Preview Section */}
        <section id="preview" className="py-24 px-4 sm:px-6 bg-white relative" data-id="element-109">
          <div className="absolute top-0 left-0 w-full h-24 bg-white" data-id="element-110"></div>
          {/* Decorative elements */}
          <div className="absolute top-1/4 left-1/2 w-64 h-64 bg-[#D93B28]/5 rounded-full blur-xl transform -translate-x-1/2" data-id="element-111"></div>
          <div className="absolute bottom-1/3 right-1/4 w-48 h-48 bg-[#D93B28]/5 rounded-full blur-xl" data-id="element-112"></div>
          <div className="container mx-auto max-w-6xl" data-id="element-113">
            {/* Section title */}
            <div className="text-center mb-12" data-id="element-114">
              <Pill className="mb-4" data-id="element-115">
                <span className="text-[#D93B28] font-medium" data-id="element-116">{t.preview}</span>
              </Pill>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-4 mb-6 tracking-tight" data-id="element-117">
                {t.powerfulDashboard}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-118">
                {t.completeVisibility}
              </p>
            </div>
            {/* Preview card */}
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden transform hover:scale-[1.02] transition-all duration-500 hover:shadow-[0_20px_50px_rgba(217,59,40,0.2)] relative group" data-id="element-119">
              {/* Glow effect with solid color */}
              <div className="absolute inset-0 -z-10 bg-[#D93B28]/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-120"></div>
              {/* Overlay with solid color */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-121"></div>
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/1rpeZ8g1JGdxzmqGpdHoxW/image.png" alt="Logix Dashboard Preview" className="w-full h-auto" data-id="element-122" />
              {/* Content overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-8 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500 bg-black/80 text-white" data-id="element-123">
                <div className="flex items-center justify-between" data-id="element-124">
                  <div className="text-xs flex items-center" data-id="element-125">
                    <span className="mr-1" data-id="element-126">XS</span>
                    <span className="h-4 w-px bg-white/40" data-id="element-127"></span>
                    <span className="mx-1" data-id="element-128">SM</span>
                    <span className="h-4 w-px bg-white/40" data-id="element-129"></span>
                    <span className="mx-1" data-id="element-130">MD</span>
                    <span className="h-4 w-px bg-white/40" data-id="element-131"></span>
                    <span className="mx-1" data-id="element-132">LG</span>
                    <span className="h-4 w-px bg-white/40" data-id="element-133"></span>
                    <span className="ml-1" data-id="element-134">XL</span>
                  </div>
                  <div className="text-xs" data-id="element-135">
                    <span data-id="element-136">Responsive at every breakpoint</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Creative divider */}
        <CreativeDivider data-id="element-137" />
        {/* Features Section */}
        <section id="features" ref={featuresRef} className="py-24 px-4 sm:px-6 bg-white relative" data-id="element-138">
          {/* Decorative elements with solid colors */}
          <div className="absolute top-1/3 right-0 w-64 h-64 bg-[#D93B28]/5 rounded-full blur-xl" data-id="element-139"></div>
          <div className="absolute bottom-1/4 left-0 w-48 h-48 bg-[#D93B28]/5 rounded-full blur-xl" data-id="element-140"></div>
          <div className="container mx-auto max-w-6xl" data-id="element-141">
            <div className="text-center mb-20" style={{
            transform: `translateY(${parallaxFeatures * 0.3}px)`
          }} data-id="element-142">
              <Pill className="mb-4" data-id="element-143">
                <span className="text-[#D93B28] font-medium" data-id="element-144">{t.features}</span>
              </Pill>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6 tracking-tight" data-id="element-145">
                {t.featuresTitle1}{' '}
                <span className="relative" data-id="element-146">
                  <span className="text-[#D93B28]" data-id="element-147">{t.featuresTitle2}</span>
                  <span className="absolute -bottom-2 left-0 w-full h-2 bg-[#D93B28]/10 rounded-full" data-id="element-148"></span>
                </span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-149">
                {t.featuresSubtitle}
              </p>
            </div>
            {/* Feature cards with solid colors */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8" data-id="element-150">
              {[{
              icon: <BoxIcon data-id="element-151" />,
              title: t.orderManagement,
              description: t.orderManagementDesc,
              color: 'bg-[#D93B28]/15',
              activeColor: 'bg-[#D93B28]',
              textColor: 'text-[#D93B28]',
              indicatorColor: 'bg-[#D93B28]'
            }, {
              icon: <ZapIcon data-id="element-152" />,
              title: t.easyFulfil,
              description: t.easyFulfilDesc,
              color: 'bg-[#D34A44]/15',
              activeColor: 'bg-[#D34A44]',
              textColor: 'text-[#D34A44]',
              indicatorColor: 'bg-[#D34A44]'
            }, {
              icon: <TruckIcon data-id="element-153" />,
              title: t.shippingSolutions,
              description: t.shippingSolutionsDesc,
              color: 'bg-[#D93B28]/15',
              activeColor: 'bg-[#D93B28]',
              textColor: 'text-[#D93B28]',
              indicatorColor: 'bg-[#D93B28]'
            }, {
              icon: <ShieldIcon data-id="element-154" />,
              title: t.returnsManagement,
              description: t.returnsManagementDesc,
              color: 'bg-[#D34A44]/15',
              activeColor: 'bg-[#D34A44]',
              textColor: 'text-[#D34A44]',
              indicatorColor: 'bg-[#D34A44]'
            }, {
              icon: <BarChart3Icon data-id="element-155" />,
              title: t.inventoryControl,
              description: t.inventoryControlDesc,
              color: 'bg-[#D93B28]/15',
              activeColor: 'bg-[#D93B28]',
              textColor: 'text-[#D93B28]',
              indicatorColor: 'bg-[#D93B28]'
            }, {
              icon: <UsersIcon data-id="element-156" />,
              title: t.manageFleets,
              description: t.manageFleetsDesc,
              color: 'bg-[#D34A44]/15',
              activeColor: 'bg-[#D34A44]',
              textColor: 'text-[#D34A44]',
              indicatorColor: 'bg-[#D34A44]'
            }].map((feature, index) => <CreativeCard key={index} className="group h-full flex flex-col feature-card transform transition-all duration-500 hover:-translate-y-2" data-id="element-157">
                  <div className={`absolute ${isRTL ? '-left-12' : '-right-12'} -top-12 w-24 h-24 ${feature.color} rounded-full group-hover:scale-150 transition-all duration-500`} data-id="element-158"></div>
                  <div className={`${feature.color} w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${feature.activeColor.replace('bg-', 'group-hover:bg-')} transition-all duration-300 shadow-lg`} data-id="element-159">
                    <div className={`${feature.textColor} group-hover:text-white transition-colors duration-300`} data-id="element-160">
                      {feature.icon}
                    </div>
                  </div>
                  <h3 className={`text-xl font-semibold mb-3 relative group-hover:${feature.textColor.replace('text-', '')}`} data-id="element-161">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 flex-grow relative" data-id="element-162">
                    {feature.description}
                  </p>
                  <div className={`h-1.5 w-0 group-hover:w-full ${feature.indicatorColor} mt-4 transition-all duration-300 rounded-full`} data-id="element-163"></div>
                </CreativeCard>)}
            </div>
          </div>
        </section>
        {/* Creative divider */}
        <CreativeDivider data-id="element-164" />
        {/* Benefits Section */}
        <section id="benefits" ref={benefitsRef} className="py-24 px-4 sm:px-6 bg-gray-50 relative" data-id="element-165">
          <div className="absolute top-0 left-0 w-full h-24 bg-white" data-id="element-166"></div>
          {/* Decorative elements with solid colors */}
          <div className="absolute top-1/4 right-1/3 w-64 h-64 bg-[#D93B28]/5 rounded-full blur-xl" data-id="element-167"></div>
          <div className="absolute bottom-1/3 left-1/4 w-48 h-48 bg-[#D93B28]/5 rounded-full blur-xl" data-id="element-168"></div>
          <div className="container mx-auto max-w-6xl" data-id="element-169">
            <div className="text-center mb-20" style={{
            transform: `translateY(${parallaxBenefits * 0.3}px)`
          }} data-id="element-170">
              <Pill className="mb-4" data-id="element-171">
                <span className="text-[#D93B28] font-medium" data-id="element-172">{t.benefits}</span>
              </Pill>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6 tracking-tight" data-id="element-173">
                {t.benefitsTitle1}{' '}
                <span className="relative" data-id="element-174">
                  <span className="text-[#D93B28]" data-id="element-175">{t.benefitsTitle2}</span>
                  <span className="absolute -bottom-2 left-0 w-full h-2 bg-[#D93B28]/10 rounded-full" data-id="element-176"></span>
                </span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-177">
                {t.benefitsSubtitle}
              </p>
            </div>
            {/* Benefits list with solid colors */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-12 gap-x-10 max-w-5xl mx-auto" data-id="element-178">
              {[{
              icon: <LightbulbIcon size={24} data-id="element-179" />,
              title: t.exclusiveFeatures,
              description: t.exclusiveFeaturesDesc,
              color: '#D93B28',
              bgColor: 'bg-[#D93B28]/10',
              hoverBgColor: 'bg-[#D93B28]',
              textColor: 'text-[#D93B28]',
              borderColor: 'border-[#D93B28]'
            }, {
              icon: <ZapIcon size={24} data-id="element-180" />,
              title: t.earlyAccess,
              description: t.earlyAccessDesc,
              color: '#D34A44',
              bgColor: 'bg-[#D34A44]/10',
              hoverBgColor: 'bg-[#D34A44]',
              textColor: 'text-[#D34A44]',
              borderColor: 'border-[#D34A44]'
            }, {
              icon: <PresentationIcon size={24} data-id="element-181" />,
              title: t.priorityOnboarding,
              description: t.priorityOnboardingDesc,
              color: '#D93B28',
              bgColor: 'bg-[#D93B28]/10',
              hoverBgColor: 'bg-[#D93B28]',
              textColor: 'text-[#D93B28]',
              borderColor: 'border-[#D93B28]'
            }, {
              icon: <GemIcon size={24} data-id="element-182" />,
              title: t.specialOffers,
              description: t.specialOffersDesc,
              color: '#D34A44',
              bgColor: 'bg-[#D34A44]/10',
              hoverBgColor: 'bg-[#D34A44]',
              textColor: 'text-[#D34A44]',
              borderColor: 'border-[#D34A44]'
            }].map((benefit, index) => <div key={index} className="flex items-start group" style={{
              transform: `translateY(${parallaxBenefits * (0.1 + index * 0.05)}px)`,
              transition: 'transform 0.3s ease-out'
            }} data-id="element-183">
                  <div className={`relative ${isRTL ? 'order-last' : 'order-first'}`} data-id="element-184">
                    <div className={`absolute inset-0 -z-10 ${benefit.bgColor} blur-md rounded-2xl transform translate-y-1 opacity-0 group-hover:opacity-100 transition-opacity`} data-id="element-185"></div>
                    <div className={`${benefit.bgColor} w-16 h-16 rounded-2xl flex items-center justify-center shrink-0 ${rtlFlip('mr-6')} group-hover:${benefit.hoverBgColor} transition-all duration-300 shadow-lg transform group-hover:rotate-6 group-hover:scale-110`} data-id="element-186">
                      <div className={`${benefit.textColor} group-hover:text-white transition-colors duration-300`} data-id="element-187">
                        {benefit.icon}
                      </div>
                    </div>
                  </div>
                  <div data-id="element-188">
                    <h3 className={`text-xl font-semibold mb-3 group-hover:${benefit.textColor} transition-colors relative inline-block`} data-id="element-189">
                      {benefit.title}
                      <span className={`absolute -bottom-1 left-0 w-0 h-0.5 ${benefit.bgColor} transition-all duration-300 group-hover:w-full`} data-id="element-190"></span>
                    </h3>
                    <p className="text-gray-600" data-id="element-191">{benefit.description}</p>
                  </div>
                </div>)}
            </div>
            {/* CTA with solid colors */}
            <div className="mt-16 text-center" data-id="element-192">
              <div className="inline-flex items-center bg-white/80 backdrop-blur-sm px-6 py-3 rounded-full border border-gray-200/70 shadow-md relative overflow-hidden group" data-id="element-193">
                <div className="absolute inset-0 bg-[#D93B28]/5 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-194"></div>
                <span className={`text-gray-800 font-medium ${rtlFlip('mr-3')} relative`} data-id="element-195">
                  {t.readyToStart}
                </span>
                <GlowingButton onClick={openWaitlistForm} className="text-sm py-2" data-id="element-196">
                  <span className="flex items-center justify-center" data-id="element-197">
                    {t.joinWaitlist}
                    {isRTL ? <ArrowLeftIcon size={16} className="mr-2 transform group-hover:-translate-x-1 transition-transform" data-id="element-198" /> : <ArrowRightIcon size={16} className="ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-199" />}
                  </span>
                </GlowingButton>
              </div>
            </div>
          </div>
        </section>
        {/* Creative divider */}
        <CreativeDivider data-id="element-200" />
        {/* Product Screenshots Section */}
        <section className="py-24 px-4 sm:px-6 bg-white relative overflow-hidden" data-id="element-201">
          {/* Background pattern */}
          <div className="absolute inset-0 bg-[radial-gradient(#D93B28_1px,transparent_1px)] opacity-[0.03] [background-size:20px_20px]" data-id="element-202"></div>
          {/* Decorative elements with solid colors */}
          <div className="absolute top-1/4 left-1/2 w-64 h-64 bg-[#D93B28]/5 rounded-full blur-xl transform -translate-x-1/2" data-id="element-203"></div>
          <div className="absolute bottom-1/3 right-1/4 w-48 h-48 bg-[#D93B28]/5 rounded-full blur-xl" data-id="element-204"></div>
          <div className="container mx-auto max-w-6xl" data-id="element-205">
            <div className="text-center mb-16" data-id="element-206">
              <Pill className="mb-4" data-id="element-207">
                <span className="text-[#D93B28] font-medium" data-id="element-208">
                  {t.productTour}
                </span>
              </Pill>
              {/* Responsive by Design Badge */}
              <div className="flex justify-center mb-4" data-id="element-209">
                <div className="inline-flex items-center bg-[#D93B28]/10 px-4 py-2 rounded-full border border-[#D93B28]/20 shadow-sm" data-id="element-210">
                  <div className="w-5 h-5 mr-2 text-[#D93B28]" data-id="element-211">
                    <LayersIcon size={20} data-id="element-212" />
                  </div>
                  <span className="text-sm font-medium text-gray-800" data-id="element-213">
                    {t.responsiveByDesign}
                  </span>
                  <div className="ml-2 px-2 py-0.5 bg-[#D93B28]/10 rounded-full text-xs font-medium text-[#D93B28]" data-id="element-214">
                    {t.optimizedAllScreens}
                  </div>
                </div>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-4 mb-6 tracking-tight" data-id="element-215">
                {t.experienceLogix1}{' '}
                <span className="relative" data-id="element-216">
                  <span className="text-[#D93B28]" data-id="element-217">{t.experienceLogix2}</span>
                  <span className="absolute -bottom-2 left-0 w-full h-2 bg-[#D93B28]/10 rounded-full" data-id="element-218"></span>
                </span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-10" data-id="element-219">
                {t.manageLogistics}
              </p>
            </div>
            {/* Responsive showcase */}
            <div className="relative mb-16" data-id="element-220">
              <div className="bg-white rounded-3xl shadow-2xl overflow-hidden relative group" data-id="element-221">
                {/* Glow effect with solid color */}
                <div className="absolute inset-0 -z-10 bg-[#D93B28]/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-222"></div>
                {/* Devices mockup */}
                <div className="relative p-6 flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8" data-id="element-223">
                  {/* Laptop */}
                  <div className="relative w-full md:w-1/2 transform transition-all duration-700 group-hover:scale-105" data-id="element-224">
                    <div className="bg-gray-800 rounded-t-xl p-2 pb-0" data-id="element-225">
                      <div className="flex space-x-1 mb-1" data-id="element-226">
                        <div className="w-2 h-2 rounded-full bg-red-500" data-id="element-227"></div>
                        <div className="w-2 h-2 rounded-full bg-yellow-500" data-id="element-228"></div>
                        <div className="w-2 h-2 rounded-full bg-green-500" data-id="element-229"></div>
                      </div>
                      <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kfCMSzVohVLcoskHVJunuc/image.png" alt="Logix on laptop" className="rounded-t border-t border-gray-700 w-full" data-id="element-230" />
                    </div>
                    <div className="bg-gray-800 h-3 rounded-b-xl" data-id="element-231"></div>
                    <div className="bg-gray-700 h-2 mx-auto w-1/3 rounded-b-xl" data-id="element-232"></div>
                  </div>
                  <div className="flex flex-col md:flex-row items-center gap-4" data-id="element-233">
                    {/* Tablet */}
                    <div className="relative transform transition-all duration-700 group-hover:scale-105 group-hover:rotate-1" data-id="element-234">
                      <div className="bg-gray-800 rounded-xl p-2 w-40 md:w-48" data-id="element-235">
                        <div className="absolute top-1/2 -left-1 w-1 h-6 bg-gray-700 rounded-l-lg transform -translate-y-1/2" data-id="element-236"></div>
                        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kfCMSzVohVLcoskHVJunuc/image.png" alt="Logix on tablet" className="rounded border border-gray-700 w-full" data-id="element-237" />
                        <div className="absolute bottom-1/2 transform translate-y-1/2 left-0 right-0 flex justify-center" data-id="element-238">
                          <div className="w-3 h-3 rounded-full border-2 border-gray-700" data-id="element-239"></div>
                        </div>
                      </div>
                    </div>
                    {/* Mobile */}
                    <div className="relative transform transition-all duration-700 group-hover:scale-105 group-hover:-rotate-1" data-id="element-240">
                      <div className="bg-gray-800 rounded-xl p-1 w-24 md:w-28" data-id="element-241">
                        <div className="absolute top-4 -right-1 w-1 h-4 bg-gray-700 rounded-r-lg" data-id="element-242"></div>
                        <div className="absolute top-10 -right-1 w-1 h-4 bg-gray-700 rounded-r-lg" data-id="element-243"></div>
                        <div className="w-full h-1 bg-gray-700 rounded-full mb-1" data-id="element-244"></div>
                        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kfCMSzVohVLcoskHVJunuc/image.png" alt="Logix on mobile" className="rounded border border-gray-700 w-full" data-id="element-245" />
                        <div className="w-6 h-1 bg-gray-700 rounded-full mx-auto mt-1" data-id="element-246"></div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Breakpoints indicator */}
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-247">
                  <div className="flex justify-between items-center" data-id="element-248">
                    <div className="text-xs flex items-center" data-id="element-249">
                      <span className="mr-1" data-id="element-250">XS</span>
                      <span className="h-4 w-px bg-white/40" data-id="element-251"></span>
                      <span className="mx-1" data-id="element-252">SM</span>
                      <span className="h-4 w-px bg-white/40" data-id="element-253"></span>
                      <span className="mx-1" data-id="element-254">MD</span>
                      <span className="h-4 w-px bg-white/40" data-id="element-255"></span>
                      <span className="mx-1" data-id="element-256">LG</span>
                      <span className="h-4 w-px bg-white/40" data-id="element-257"></span>
                      <span className="ml-1" data-id="element-258">XL</span>
                    </div>
                    <div className="text-xs" data-id="element-259">
                      <span data-id="element-260">Responsive at every breakpoint</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Feature panels with solid colors */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-261">
              {[{
              icon: <LayoutGridIcon size={20} className="text-[#D93B28]" data-id="element-262" />,
              title: t.responsiveInterface,
              description: t.responsiveInterfaceDesc,
              devices: [<SmartphoneIcon key="mobile" size={16} className="text-[#D93B28]/70" data-id="element-263" />, <TabletIcon key="tablet" size={16} className="text-[#D93B28]/70" data-id="element-264" />, <LaptopIcon key="laptop" size={16} className="text-[#D93B28]/70" data-id="element-265" />]
            }, {
              icon: <LayersIcon size={20} className="text-[#D93B28]" data-id="element-266" />,
              title: t.advancedAdminTools,
              description: t.advancedAdminToolsDesc,
              devices: [<ServerIcon key="server" size={16} className="text-[#D93B28]/70" data-id="element-267" />, <BarChart3Icon key="chart" size={16} className="text-[#D93B28]/70" data-id="element-268" />, <SettingsIcon key="settings" size={16} className="text-[#D93B28]/70" data-id="element-269" />]
            }].map((item, index) => <div key={index} className="group perspective" data-id="element-270">
                  <CreativeCard className="h-full" data-id="element-271">
                    <div className="flex items-center mb-4" data-id="element-272">
                      <div className="w-10 h-10 rounded-lg bg-[#D93B28]/10 flex items-center justify-center mr-3" data-id="element-273">
                        {item.icon}
                      </div>
                      <h3 className="text-xl font-semibold" data-id="element-274">{item.title}</h3>
                    </div>
                    <p className="text-gray-600 mb-6" data-id="element-275">{item.description}</p>
                    <div className="flex space-x-3 mt-auto" data-id="element-276">
                      {item.devices.map((device, i) => <div key={i} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-[#D93B28]/10 transition-colors" data-id="element-277">
                          {device}
                        </div>)}
                    </div>
                  </CreativeCard>
                </div>)}
            </div>
          </div>
        </section>
        {/* Creative divider */}
        <CreativeDivider data-id="element-278" />
        {/* Testimonials Section */}
        <section id="testimonials" ref={testimonialsRef} className="py-24 px-4 sm:px-6 bg-gray-50 relative" data-id="element-279">
          <div className="absolute top-0 left-0 w-full h-24 bg-white" data-id="element-280"></div>
          {/* Decorative elements with solid colors */}
          <div className="absolute top-1/3 left-0 w-64 h-64 bg-[#D93B28]/5 rounded-full blur-xl" data-id="element-281"></div>
          <div className="absolute bottom-1/4 right-0 w-48 h-48 bg-[#D93B28]/5 rounded-full blur-xl" data-id="element-282"></div>
          <div className="container mx-auto max-w-6xl" data-id="element-283">
            <div className="text-center mb-20" style={{
            transform: `translateY(${parallaxTestimonials * 0.3}px)`
          }} data-id="element-284">
              <Pill className="mb-4" data-id="element-285">
                <span className="text-[#D93B28] font-medium" data-id="element-286">
                  {t.testimonials}
                </span>
              </Pill>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6 tracking-tight" data-id="element-287">
                {t.testimonialsTitle1}{' '}
                <span className="relative" data-id="element-288">
                  <span className="text-[#D93B28]" data-id="element-289">{t.testimonialsTitle2}</span>
                  <span className="absolute -bottom-2 left-0 w-full h-2 bg-[#D93B28]/10 rounded-full" data-id="element-290"></span>
                </span>
              </h2>
            </div>
            {/* Testimonial cards with solid colors */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-291">
              {testimonials.map((testimonial, index) => <CreativeCard key={index} className="p-0 transform transition-all duration-500 hover:-translate-y-2" style={{
              transform: `translateY(${parallaxTestimonials * (0.1 + index * 0.05)}px)`,
              transition: 'transform 0.3s ease-out'
            }} data-id="element-292">
                  {/* Top highlight bar with solid color */}
                  <div className={`absolute top-0 left-0 h-1.5 w-0 group-hover:w-full ${index % 2 === 0 ? 'bg-[#D93B28]' : 'bg-[#D34A44]'} transition-all duration-500 rounded-t-xl`} data-id="element-293"></div>
                  {/* Corner decoration with solid color */}
                  <div className={`absolute top-0 ${isRTL ? 'left-0' : 'right-0'} w-20 h-20 ${index % 2 === 0 ? 'bg-[#D93B28]/10' : 'bg-[#D34A44]/10'} ${isRTL ? 'rounded-br-3xl' : 'rounded-bl-3xl'}`} data-id="element-294"></div>
                  <div className="p-8" data-id="element-295">
                    {/* Profile section with solid color */}
                    <div className="flex items-center mb-6" data-id="element-296">
                      <div className="relative" data-id="element-297">
                        <div className={`absolute inset-0 ${index % 2 === 0 ? 'bg-[#D93B28]' : 'bg-[#D34A44]'} rounded-full opacity-30`} data-id="element-298"></div>
                        {testimonial.image ? <img src={testimonial.image} alt={testimonial.name} className="w-14 h-14 rounded-full object-cover border-2 border-white relative z-10" data-id="element-299" /> : <div className="w-14 h-14 bg-gray-200 rounded-full border-2 border-white flex items-center justify-center relative z-10" data-id="element-300">
                            {testimonial.icon}
                          </div>}
                      </div>
                      <div className={rtlFlip('ml-4')} data-id="element-301">
                        <h4 className="font-semibold" data-id="element-302">{testimonial.name}</h4>
                        <p className="text-sm text-gray-500" data-id="element-303">
                          {testimonial.role}
                        </p>
                      </div>
                    </div>
                    {/* Rating stars with solid color quote marks */}
                    <div className="mb-4 flex" data-id="element-304">
                      {[...Array(5)].map((_, i) => <StarIcon key={i} size={16} className={`${index % 2 === 0 ? 'text-[#D93B28]' : 'text-[#D34A44]'} ${rtlFlip('mr-0.5')}`} fill={index % 2 === 0 ? '#D93B28' : '#D34A44'} data-id="element-305" />)}
                    </div>
                    {/* Quote with solid color quote marks */}
                    <div className="relative" data-id="element-306">
                      <div className={`absolute -top-2 ${isRTL ? '-right-2' : '-left-2'} ${index % 2 === 0 ? 'text-[#D93B28]' : 'text-[#D34A44]'}/30 text-4xl font-serif`} data-id="element-307">
                        "
                      </div>
                      <div className={`absolute -bottom-6 ${isRTL ? '-left-2' : '-right-2'} ${index % 2 === 0 ? 'text-[#D93B28]' : 'text-[#D34A44]'}/30 text-4xl font-serif rotate-180`} data-id="element-308">
                        "
                      </div>
                      <p className="text-gray-600 italic relative z-10 px-2" data-id="element-309">
                        {testimonial.quote}
                      </p>
                    </div>
                  </div>
                </CreativeCard>)}
            </div>
          </div>
        </section>
        {/* Creative divider */}
        <CreativeDivider data-id="element-310" />
        {/* Final CTA Section */}
        <section className="py-24 px-4 sm:px-6 bg-white relative overflow-hidden" ref={ctaRef} data-id="element-311">
          {/* Decorative elements with solid colors */}
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-[#D93B28]/10 rounded-full blur-3xl" data-id="element-312"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[#D34A44]/10 rounded-full blur-3xl" data-id="element-313"></div>
          {/* Floating icons */}
          <div className="absolute top-1/4 left-1/5 w-8 h-8 text-[#D93B28]/30 animate-float" style={{
          animationDuration: '7s'
        }} data-id="element-314">
            <MailIcon data-id="element-315" />
          </div>
          <div className="absolute bottom-1/3 right-1/5 w-8 h-8 text-[#D93B28]/30 animate-float" style={{
          animationDuration: '9s',
          animationDelay: '1s'
        }} data-id="element-316">
            <RocketIcon data-id="element-317" />
          </div>
          <div className="container mx-auto max-w-6xl text-center relative" style={{
          transform: `translateY(${parallaxCta * 0.2}px)`
        }} data-id="element-318">
            {/* CTA card with solid colors */}
            <div className="bg-white/90 backdrop-blur-xl p-10 rounded-3xl shadow-2xl max-w-3xl mx-auto relative overflow-hidden border border-white/50" data-id="element-319">
              {/* Top highlight bar with solid colors */}
              <div className="absolute top-0 left-0 w-1/2 h-2 bg-[#D93B28]" data-id="element-320"></div>
              <div className="absolute top-0 right-0 w-1/2 h-2 bg-[#D34A44]" data-id="element-321"></div>
              {/* Corner decorations with solid colors */}
              <div className={`absolute -top-20 ${isRTL ? '-left-20' : '-right-20'} w-40 h-40 bg-[#D93B28]/10 rounded-full`} data-id="element-322"></div>
              <div className={`absolute -bottom-20 ${isRTL ? '-right-20' : '-left-20'} w-40 h-40 bg-[#D34A44]/10 rounded-full`} data-id="element-323"></div>
              {/* Title with solid colors */}
              <div className="relative" data-id="element-324">
                <SparkleIcon size={24} className={`text-[#D93B28] absolute -top-6 ${isRTL ? '-right-6' : '-left-6'} animate-pulse`} data-id="element-325" />
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 tracking-tight" data-id="element-326">
                  {t.joinToday1}{' '}
                  <span className="text-[#D93B28]" data-id="element-327">{t.joinToday2}</span>
                </h2>
                <SparkleIcon size={24} className={`text-[#D34A44] absolute -bottom-6 ${isRTL ? '-left-6' : '-right-6'} animate-pulse`} style={{
                animationDelay: '1s'
              }} data-id="element-328" />
              </div>
              <p className="text-lg text-gray-600 max-w-xl mx-auto mb-8" data-id="element-329">
                {t.ctaSubtitle}
              </p>
              {/* Email form */}
              <form onSubmit={handleSubmit} className="flex flex-col md:flex-row items-center justify-center gap-4 max-w-lg mx-auto" data-id="element-330">
                <div className="relative w-full md:flex-1" data-id="element-331">
                  <NeumorphicInput type="email" placeholder={t.emailJoinPlaceholder} value={email} onChange={e => setEmail(e.target.value)} required={true} data-id="element-332" />
                </div>
                <GlowingButton onClick={() => {}} className="w-full md:w-auto" data-id="element-333">
                  <span className="flex items-center justify-center" data-id="element-334">
                    {t.joinWaitlist}
                    {isRTL ? <ArrowLeftIcon size={16} className="mr-2 transform group-hover:-translate-x-1 transition-transform" data-id="element-335" /> : <ArrowRightIcon size={16} className="ml-2 transform group-hover:translate-x-1 transition-transform" data-id="element-336" />}
                  </span>
                </GlowingButton>
              </form>
              <div className="mt-8 text-gray-500 text-sm" data-id="element-337">
                {t.byJoining}{' '}
                <Link to={language === 'ar' ? '/terms-ar' : '/terms'} className="text-[#D93B28] hover:underline" data-id="element-338">
                  {t.termsOfService}
                </Link>{' '}
                {t.and}{' '}
                <Link to={language === 'ar' ? '/privacy-ar' : '/privacy'} className="text-[#D93B28] hover:underline" data-id="element-339">
                  {t.privacyPolicy}
                </Link>
              </div>
            </div>
          </div>
        </section>
        {/* Creative divider */}
        <CreativeDivider data-id="element-340" />
        {/* FAQ Section */}
        <section id="faq" className="py-24 px-4 sm:px-6 bg-gray-50 relative" data-id="element-341">
          {/* Decorative elements with solid colors */}
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-[#D93B28]/5 rounded-full blur-xl" data-id="element-342"></div>
          <div className="absolute bottom-1/3 left-1/4 w-48 h-48 bg-[#D93B28]/5 rounded-full blur-xl" data-id="element-343"></div>
          <div className="container mx-auto max-w-3xl" data-id="element-344">
            <div className="text-center mb-16" data-id="element-345">
              <Pill className="mb-4" data-id="element-346">
                <span className="text-[#D93B28] font-medium" data-id="element-347">{t.faq}</span>
              </Pill>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6 tracking-tight" data-id="element-348">
                {t.faqTitle1}{' '}
                <span className="relative" data-id="element-349">
                  <span className="text-[#D93B28]" data-id="element-350">{t.faqTitle2}</span>
                  <span className="absolute -bottom-2 left-0 w-full h-2 bg-[#D93B28]/10 rounded-full" data-id="element-351"></span>
                </span>
              </h2>
              <p className="text-lg text-gray-600" data-id="element-352">{t.faqSubtitle}</p>
            </div>
            {/* FAQ accordion with solid colors */}
            <div className="space-y-6" data-id="element-353">
              {faqs.map((faq, index) => <div key={index} className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-lg hover:shadow-xl transition-all group" data-id="element-354">
                  <button className="flex items-center justify-between w-full px-6 py-5 text-left hover:bg-gray-50 transition-colors" onClick={() => toggleFaq(index)} aria-expanded={faqOpen === index} aria-controls={`faq-panel-${index}`} data-id="element-355">
                    <span className={`font-semibold text-gray-900 group-hover:${index % 2 === 0 ? 'text-[#D93B28]' : 'text-[#D34A44]'} transition-colors`} data-id="element-356">
                      {faq.question}
                    </span>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${faqOpen === index ? index % 2 === 0 ? 'bg-[#D93B28]' : 'bg-[#D34A44]' : 'bg-gray-200'} ${faqOpen === index ? 'rotate-180' : ''}`} data-id="element-357">
                      <ChevronDownIcon className="text-white transition-transform" size={16} data-id="element-358" />
                    </div>
                  </button>
                  <div id={`faq-panel-${index}`} className={`transition-all duration-300 ease-in-out ${faqOpen === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`} data-id="element-359">
                    <div className="px-6 py-5 bg-white border-t border-gray-100" data-id="element-360">
                      <p className="text-gray-600" data-id="element-361">{faq.answer}</p>
                    </div>
                  </div>
                </div>)}
            </div>
            {/* Support CTA with solid colors */}
            <div className="mt-12 text-center" data-id="element-362">
              <p className="text-gray-600 mb-4" data-id="element-363">{t.stillHaveQuestions}</p>
              <button className="px-6 py-3 bg-white border border-gray-200 rounded-full text-gray-800 font-medium hover:shadow-md transition-all flex items-center mx-auto group relative overflow-hidden" data-id="element-364">
                <div className="absolute inset-0 bg-[#D93B28]/5 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-365"></div>
                <span className="relative z-10 flex items-center" data-id="element-366">
                  {t.contactSupport}
                  <ExternalLinkIcon size={16} className={`${rtlFlip('ml-2')} text-[#D93B28] transform group-hover:translate-x-1 transition-transform`} data-id="element-367" />
                </span>
              </button>
            </div>
          </div>
        </section>
      </main>
      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-16 relative z-10" data-id="element-368">
        <div className="container mx-auto px-4 sm:px-6" data-id="element-369">
          <div className="flex flex-col md:flex-row items-center justify-between" data-id="element-370">
            {/* Logo */}
            <div className="flex items-center mb-8 md:mb-0" data-id="element-371">
              <div className="relative group" data-id="element-372">
                <div className="absolute inset-0 bg-[#D93B28]/20 rounded-lg blur-md group-hover:blur-xl transition-all duration-300" data-id="element-373"></div>
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2FH5qpVtZh9D5YZVGYt4z6/1762799569364-removebg-preview.png" alt="Logix Logo" className={`h-12 w-auto relative z-10 transition-all duration-300`} data-id="element-374" />
              </div>
            </div>
            {/* Copyright */}
            <div className="mb-8 md:mb-0 text-center md:text-left" data-id="element-375">
              <p className="text-gray-500" data-id="element-376">{t.allRightsReserved}</p>
            </div>
            {/* Social links */}
            <div className="flex space-x-4" data-id="element-377">
              {[{
              icon: <FacebookIcon size={20} data-id="element-378" />,
              label: 'Facebook'
            }, {
              icon: <TwitterIcon size={20} data-id="element-379" />,
              label: 'Twitter'
            }, {
              icon: <InstagramIcon size={20} data-id="element-380" />,
              label: 'Instagram'
            }, {
              icon: <LinkedinIcon size={20} data-id="element-381" />,
              label: 'LinkedIn'
            }, {
              icon: <YoutubeIcon size={20} data-id="element-382" />,
              label: 'YouTube'
            }].map((social, index) => <a key={index} href="#" className="text-gray-400 hover:text-[#D93B28] transition-all p-2 rounded-full hover:bg-gray-100 group relative" aria-label={social.label} data-id="element-383">
                  <div className="absolute inset-0 bg-[#D93B28]/5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-384"></div>
                  <div className="transform group-hover:scale-110 transition-transform relative z-10" data-id="element-385">
                    {social.icon}
                  </div>
                </a>)}
            </div>
          </div>
        </div>
      </footer>
    </div>;
}